import type { TFunction } from 'i18next';

import type { ExtractedLink } from '../../../utils/markdownLinks';
import { AnalysisSubHeader, TwoColumnProse } from '../../../components';
import { useTranslation } from 'react-i18next';

interface ExecutiveSummaryProps {
  text?: string;
  /** The summary's sources; not yet on the DTO, so the pill stays hidden until the backend supplies them. */
  sources?: ExtractedLink[];
}

export const ExecutiveSummary = ({ text, sources }: ExecutiveSummaryProps) => {
  const { t } = useTranslation('analysis');

  if (!text) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.executiveSummary.executiveSummary')}
        subtitle={t('analysisResult.executiveSummary.marketPositionProfitabilityBrandPosture')}
      />
      <TwoColumnProse text={text} />
      {sources && sources.length > 0 && <SummarySources sources={sources} t={t} />}
    </div>
  );
};

const MAX_VISIBLE_SOURCES = 3;

const SummarySources = ({ sources, t }: { sources: ExtractedLink[]; t: TFunction<'analysis'> }) => {
  const unique = [...new Map(sources.map((source) => [source.url, source])).values()];
  const visible = unique.slice(0, MAX_VISIBLE_SOURCES);
  const overflow = unique.length - visible.length;
  const [primary] = unique;

  return (
    <div className="flex w-fit items-center gap-2 rounded-full border border-border-light py-1 pr-3 pl-1">
      <div className="flex items-center -space-x-1.5">
        {visible.map((source) => (
          <span
            key={source.url}
            className="flex size-6 items-center justify-center rounded-full border border-background bg-stone-100 text-label-sm font-medium text-muted-foreground uppercase"
          >
            {source.domain.charAt(0)}
          </span>
        ))}
      </div>
      <a
        href={primary.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-body-sm text-foreground"
      >
        {primary.domain}
      </a>
      {overflow > 0 && (
        <span className="text-body-sm text-muted-foreground">
          {t('analysisResult.executiveSummary.moreSources', { count: overflow })}
        </span>
      )}
    </div>
  );
};
